/* Google Analytics 4.
 *
 * One tag for the whole site. Because this is a single-page app, gtag's
 * automatic page view only ever fires once — on the very first load — so
 * automatic page views are switched off here and <AnalyticsListener />
 * sends one on every route change instead.
 *
 * Everything below funnels through `send()`, which is a no-op when the
 * tag hasn't loaded. That way a call site never has to check whether
 * analytics is available, and an ad blocker can't break the shop.
 *
 * Environment:
 *   REACT_APP_GA_MEASUREMENT_ID — override the property (e.g. a separate
 *                                 one for staging). Defaults to the live
 *                                 property below.
 *   REACT_APP_GA_DEBUG=true     — also track localhost, and log every
 *                                 event to the console. Off by default so
 *                                 development traffic stays out of the
 *                                 reports.
 */

const LIVE_MEASUREMENT_ID = 'G-FCQS9YG0H8';

export const GA_MEASUREMENT_ID =
  process.env.REACT_APP_GA_MEASUREMENT_ID || LIVE_MEASUREMENT_ID;

const DEBUG = process.env.REACT_APP_GA_DEBUG === 'true';

const CURRENCY = 'INR';

let started = false;

const isLocalhost = () =>
  typeof window !== 'undefined' &&
  /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);

/* The admin panel is the shop owner's own traffic — counting it would
   bury the customer numbers this whole thing exists to show. */
export const isAdminPath = (path = '') => path.startsWith('/admin');

const enabled = () =>
  typeof window !== 'undefined' &&
  Boolean(GA_MEASUREMENT_ID) &&
  (DEBUG || !isLocalhost());

/**
 * Loads gtag.js once. Called from index.js before the app renders.
 */
export function initAnalytics() {
  if (started || !enabled()) return;
  started = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // gtag pushes the arguments object itself — a rest-parameter version
  // sends an array and GA silently ignores it.
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    // <AnalyticsListener /> sends these; see the note at the top.
    send_page_view: false,
    debug_mode: DEBUG,
  });
}

/* Every event in this file goes through here. */
function send(name, params = {}) {
  if (DEBUG) console.log('[ga]', name, params);
  if (!enabled() || typeof window.gtag !== 'function') return;

  // Undefined values show up as "(not set)" in GA — drop them instead.
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null)
  );

  window.gtag('event', name, clean);
}

/**
 * Escape hatch for one-off events that don't have a helper below.
 * `name` must be snake_case and at most 40 characters (a GA4 rule).
 */
export const trackEvent = (name, params) => send(name, params);

/* ------------------------------------------------------------------ */
/* Pages and sections                                                  */
/* ------------------------------------------------------------------ */

/**
 * One page view per route change. `path` includes the query string so a
 * filtered shop view (/shop?planet=Venus) is its own row in the report.
 */
export function trackPageView({ path, title, section }) {
  if (isAdminPath(path)) return;

  send('page_view', {
    page_path: path,
    page_title: title,
    page_location:
      typeof window !== 'undefined' ? window.location.href : undefined,
    site_section: section,
  });
}

/**
 * A block of the home page scrolling into view — the only way to tell
 * how far down the page people actually get.
 */
export const trackSectionView = (section) =>
  send('view_section', { site_section: section });

/* ------------------------------------------------------------------ */
/* Products                                                            */
/* ------------------------------------------------------------------ */

/* GA4's ecommerce item shape. The shop's own product objects carry both
   a product_code and a Supabase uuid; item_id uses the code because
   that's what the order and the admin product list show. */
export const gaItem = (product = {}, quantity = 1) => ({
  item_id: String(product.product_code || product.id || ''),
  item_name: product.name,
  item_brand: 'Celestial Stones',
  item_category: product.category || undefined,
  item_variant: product.planet || undefined,
  price: Number(product.price) || 0,
  quantity,
});

const linesToItems = (lines = []) =>
  lines.map((line) => gaItem(line.product, line.quantity));

const linesValue = (lines = []) =>
  lines.reduce(
    (sum, line) => sum + (Number(line.product?.price) || 0) * line.quantity,
    0
  );

/** A grid of products rendered — the shop page, or the home page row. */
export const trackViewItemList = (listName, products = []) =>
  send('view_item_list', {
    item_list_name: listName,
    items: products.slice(0, 20).map((p) => gaItem(p)),
  });

/** A product card clicked open. */
export const trackSelectItem = (product, listName) =>
  send('select_item', {
    item_list_name: listName,
    items: [gaItem(product)],
  });

/** The full product modal opened. */
export const trackViewItem = (product) =>
  send('view_item', {
    currency: CURRENCY,
    value: Number(product?.price) || 0,
    items: [gaItem(product)],
  });

export const trackAddToCart = (product, quantity = 1, listName) =>
  send('add_to_cart', {
    currency: CURRENCY,
    value: (Number(product?.price) || 0) * quantity,
    item_list_name: listName,
    items: [gaItem(product, quantity)],
  });

export const trackRemoveFromCart = (product, quantity = 1) =>
  send('remove_from_cart', {
    currency: CURRENCY,
    value: (Number(product?.price) || 0) * quantity,
    items: [gaItem(product, quantity)],
  });

/** Shop filters — which planets and categories people actually browse. */
export const trackFilter = (filterType, value) =>
  send('filter_products', { filter_type: filterType, filter_value: value });

/**
 * What people type into the shop's search box, and how many pieces came
 * back. A term with result_count = 0 is the valuable one — it's a
 * customer asking for something the shop doesn't stock, or doesn't
 * name the way they do.
 */
export const trackSearch = (searchTerm, resultCount) =>
  send('search', { search_term: searchTerm, result_count: resultCount });

/* ------------------------------------------------------------------ */
/* Cart and checkout                                                   */
/* ------------------------------------------------------------------ */

export const trackViewCart = (lines = []) =>
  send('view_cart', {
    currency: CURRENCY,
    value: linesValue(lines),
    items: linesToItems(lines),
  });

export const trackBeginCheckout = (lines = [], origin) =>
  send('begin_checkout', {
    currency: CURRENCY,
    value: linesValue(lines),
    checkout_origin: origin, // 'cart' or 'buy_now'
    items: linesToItems(lines),
  });

/**
 * The order is in the database at this point — WhatsApp is only how it
 * gets confirmed, so this is the real conversion.
 */
export const trackPurchase = ({ orderNumber, lines = [], value, coupon }) =>
  send('purchase', {
    transaction_id: orderNumber,
    currency: CURRENCY,
    // What the customer actually pays, after any discount code.
    value: value ?? linesValue(lines),
    shipping: 0,
    coupon,
    items: linesToItems(lines),
  });

/* ------------------------------------------------------------------ */
/* Planets — "Shop by Planet" and the birth-date guide                 */
/* ------------------------------------------------------------------ */

export const trackPlanetOpen = (planetName, source) =>
  send('select_planet', { planet: planetName, source });

/** The planet modal's "Shop <planet> stones" call to action. */
export const trackPlanetShopClick = (planetName) =>
  send('planet_shop_click', { planet: planetName });

/** Know Your Planet: opened, or a birth date actually calculated. */
export const trackPlanetGuideOpen = (source) =>
  send('planet_guide_open', { source });

export const trackPlanetGuideResult = (planetName, basicNumber) =>
  send('planet_guide_result', {
    planet: planetName,
    basic_number: basicNumber,
  });

/* ------------------------------------------------------------------ */
/* Contact, account, consultations                                     */
/* ------------------------------------------------------------------ */

/** channel: 'whatsapp' | 'email' | 'instagram'; location: where on site. */
export const trackContactClick = (channel, location) =>
  send('contact_click', { channel, contact_location: location });

export const trackLogin = (method = 'password') => send('login', { method });

export const trackSignUp = (method = 'password') => send('sign_up', { method });

export const trackConsultationRequest = (topic) =>
  send('consultation_request', { consultation_topic: topic });
